import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  Offer,
  OfferPayload,
  OfferResponse,
  OffersFilter,
  OfferStatus,
} from "../types/offers.types";

type OfferDetailResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: Offer;
};

type OfferMutationResponse = {
  success: boolean;
  statusCode: number;
  message: string;
  resData: unknown;
};

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.offers;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const offersService = {
  async list(filter: OffersFilter): Promise<OfferResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<OfferResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load offers due to an unexpected error.",
      ) as unknown as OfferResponse;
    }
  },

  async details(id: number | string): Promise<OfferDetailResponse> {
    try {
      return await apiClient<OfferDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load offer details due to an unexpected error.",
      ) as unknown as OfferDetailResponse;
    }
  },

  async detailsByCode(code: string): Promise<OfferDetailResponse> {
    try {
      return await apiClient<OfferDetailResponse>(
        endpoints.getOfferByCode(encodeURIComponent(code)),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load offer details due to an unexpected error.",
      ) as unknown as OfferDetailResponse;
    }
  },

  async create(payload: OfferPayload): Promise<OfferMutationResponse> {
    try {
      return await apiClient<OfferMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create offer due to an unexpected error.",
      ) as OfferMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<OfferPayload>,
  ): Promise<OfferMutationResponse> {
    try {
      return await apiClient<OfferMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update offer due to an unexpected error.",
      ) as OfferMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: OfferStatus },
  ): Promise<OfferMutationResponse> {
    return offersService.update(id, payload);
  },

  async delete(id: number | string): Promise<OfferMutationResponse> {
    try {
      return await apiClient<OfferMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete offer due to an unexpected error.",
      ) as OfferMutationResponse;
    }
  },
};
